import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import EmailCaptureModal from '../EmailCaptureModal';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiDollarSign, FiUsers, FiClock, FiAward, FiHeart, FiMail, FiInstagram, FiCalendar, FiLock, FiHelpCircle, FiBarChart2 } = FiIcons;

// Create a separate variable for FiVolunteers instead of aliasing in the destructuring
const FiVolunteers = FiUsers;

const BrandingResultsSection = ({ results, inputs }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showROIHelp, setShowROIHelp] = useState(false);
  const [showPaybackHelp, setShowPaybackHelp] = useState(false);
  const [showNetValueHelp, setShowNetValueHelp] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(num);
  };

  const formatPercent = (value) => {
    // Ensure value is a number and handle potential NaN or Infinity
    const numValue = Number(value);
    if (isNaN(numValue) || !isFinite(numValue)) {
      return '0%';
    }
    
    // If value is 0, return 0%
    if (numValue === 0) {
      return '0%';
    }
    
    // Round to nearest 0.5%
    const roundedValue = Math.round(numValue * 2) / 2;
    
    // Format as percentage with one decimal place if it's a half percent
    const hasHalf = roundedValue % 1 !== 0;
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: hasHalf ? 1 : 0,
      maximumFractionDigits: hasHalf ? 1 : 0,
    }).format(roundedValue / 100);
  };

  // All possible benefit items
  const allBenefitItems = [
    {
      label: 'Donor Retention Value',
      value: results.retentionImprovement,
      icon: FiHeart,
      color: 'text-red-600 bg-red-50'
    },
    {
      label: 'New Donor Acquisition',
      value: results.newDonorValue,
      icon: FiUsers,
      color: 'text-green-600 bg-green-50'
    },
    {
      label: 'Grant Success Improvement',
      value: results.grantSuccessImprovement,
      icon: FiAward,
      color: 'text-indigo-600 bg-indigo-50'
    },
    {
      label: 'Staff Time Savings',
      value: results.annualTimeSavings,
      icon: FiClock,
      color: 'text-blue-600 bg-blue-50'
    },
    {
      label: 'Social Media Value',
      value: results.socialMediaValue,
      icon: FiInstagram,
      color: 'text-purple-600 bg-purple-50'
    },
    {
      label: 'Email Growth Value',
      value: results.emailListValue,
      icon: FiMail,
      color: 'text-orange-600 bg-orange-50'
    },
    {
      label: 'Event Improvement',
      value: results.eventImprovement,
      icon: FiCalendar,
      color: 'text-teal-600 bg-teal-50'
    },
    {
      label: 'Volunteer Value',
      value: results.volunteerValue,
      icon: FiVolunteers,
      color: 'text-amber-600 bg-amber-50'
    }
  ];

  // Only show 3 benefits in preview mode
  const previewBenefitItems = allBenefitItems.slice(0, 3);

  // Calculate brand strength improvement percentage
  const brandStrengthImprovement = results.currentBrandStrength && results.improvedBrandStrength
    ? ((results.improvedBrandStrength - results.currentBrandStrength) / results.currentBrandStrength) * 100
    : 0;

  // Calculate preview benefits total
  const previewBenefitsTotal = previewBenefitItems.reduce((sum, item) => sum + (item.value || 0), 0);

  const handleEmailSubmit = async ({ email, firstName }) => {
    console.log("Submitting branding calculator data:", {
      email,
      firstName,
      calculatorType: 'branding',
      operatingBudget: inputs.annualOperatingBudget,
      brandingBudget: inputs.currentBrandingBudget,
      brandingInvestment: inputs.brandingInvestment,
      threeYearValue: results.threeYearValue,
      paybackMonths: results.paybackMonths,
      brandStrength: results.currentBrandStrength * 10,
    });
    
    // Close the modal after successful submission
    setTimeout(() => setShowEmailModal(false), 2000);
    return true;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      {/* Key Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl shadow-lg p-8 text-white"
      >
        <h3 className="text-2xl font-bold mb-6">Your Branding ROI Opportunity</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center relative group">
            <button 
              className="absolute right-0 top-0 p-1 text-purple-200 hover:text-white"
              onClick={() => setShowROIHelp(!showROIHelp)}
              aria-label="Show ROI explanation"
            >
              <SafeIcon icon={FiHelpCircle} />
            </button>
            {showROIHelp && (
              <div className="absolute z-10 bg-white text-gray-800 p-3 rounded-lg shadow-lg text-sm w-64 left-1/2 -translate-x-1/2 top-full mt-2">
                <p>Return on Investment (ROI) shows the percentage return you'll get from your branding investment. For example, 200% means you'll get back double your investment in annual benefits.</p>
                <button 
                  className="absolute right-1 top-1 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowROIHelp(false)}
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-xs" />
                </button>
              </div>
            )}
            <div className="text-3xl font-bold">{formatNumber(results.roi || 0)}%</div>
            <div className="text-purple-100">Return on Investment</div>
          </div>
          <div className="text-center relative group">
            <button 
              className="absolute right-0 top-0 p-1 text-purple-200 hover:text-white"
              onClick={() => setShowPaybackHelp(!showPaybackHelp)}
              aria-label="Show payback explanation"
            >
              <SafeIcon icon={FiHelpCircle} />
            </button>
            {showPaybackHelp && (
              <div className="absolute z-10 bg-white text-gray-800 p-3 rounded-lg shadow-lg text-sm w-64 left-1/2 -translate-x-1/2 top-full mt-2">
                <p>Months to Payback shows how quickly your branding investment will pay for itself through increased donor retention, audience growth, and efficiency. After this period, all benefits are pure gain.</p>
                <button 
                  className="absolute right-1 top-1 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPaybackHelp(false)}
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-xs" />
                </button>
              </div>
            )}
            <div className="text-3xl font-bold">{formatNumber(results.paybackMonths || 0)}</div>
            <div className="text-purple-100">Months to Payback</div>
          </div>
          <div className="text-center relative group">
            <button 
              className="absolute right-0 top-0 p-1 text-purple-200 hover:text-white"
              onClick={() => setShowNetValueHelp(!showNetValueHelp)}
              aria-label="Show net value explanation"
            >
              <SafeIcon icon={FiHelpCircle} />
            </button>
            {showNetValueHelp && (
              <div className="absolute z-10 bg-white text-gray-800 p-3 rounded-lg shadow-lg text-sm w-64 left-1/2 -translate-x-1/2 top-full mt-2">
                <p>3-Year Net Value is the total financial benefit over three years minus your initial investment. This shows the long-term impact of your branding investment.</p>
                <button 
                  className="absolute right-1 top-1 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowNetValueHelp(false)}
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-xs" />
                </button>
              </div>
            )}
            <div className="text-3xl font-bold">{formatCurrency(results.threeYearValue || 0)}</div>
            <div className="text-purple-100">3-Year Net Value</div>
          </div>
        </div>
      </motion.div>

      {/* ROI Explanation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-indigo-50 p-5 rounded-xl"
      >
        <div className="flex items-start">
          <SafeIcon icon={FiBarChart2} className="text-indigo-600 mt-1 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-gray-800 mb-1">How We Calculate Your Branding ROI</h4>
            <p className="text-sm text-gray-700">
              Your branding investment creates returns through improved donor retention, new donor acquisition, 
              increased grant success, staff time savings, and improved audience engagement across channels. 
              A stronger brand builds trust that directly impacts your bottom line.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Brand Strength Improvement */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-lg p-6"
      >
        <h4 className="text-lg font-bold text-gray-900 mb-4">Your Brand's Untapped Potential</h4>
        
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-600">Current Brand Strength</span>
          <span className="font-medium">{Math.round(results.currentBrandStrength * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
          <div
            className="h-2.5 rounded-full bg-gray-500"
            style={{ width: `${results.currentBrandStrength * 100}%` }}
          ></div>
        </div>
        
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-600">Improved Brand Strength</span>
          <span className="font-medium">{Math.round(results.improvedBrandStrength * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
          <div
            className="h-2.5 rounded-full bg-indigo-600"
            style={{ width: `${results.improvedBrandStrength * 100}%` }}
          ></div>
        </div>
        
        <div className="bg-indigo-50 p-3 rounded-lg text-center">
          <span className="text-indigo-800 font-medium flex items-center justify-center">
            <SafeIcon icon={FiTrendingUp} className="mr-1 text-indigo-600" />
            {formatNumber(brandStrengthImprovement)}% Potential Growth Unlocked
          </span>
        </div>
      </motion.div>

      {/* Benefits Breakdown - PREVIEW VERSION */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-lg p-8"
      >
        <h4 className="text-xl font-bold text-gray-900 mb-6">Financial Benefits You're Missing Annually</h4>
        
        <div className="space-y-4">
          {/* Preview benefits (just 3) */}
          {previewBenefitItems.map((item, index) => (
            item.value ? (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${item.color}`}>
                    <SafeIcon icon={item.icon} className="text-lg" />
                  </div>
                  <span className="font-medium text-gray-700">{item.label}</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {formatCurrency(item.value || 0)}
                </span>
              </motion.div>
            ) : null
          ))}

          {/* Locked benefits preview */}
          <div
            className="border border-gray-200 rounded-lg p-4 bg-gray-50 cursor-pointer"
            onClick={() => setShowEmailModal(true)}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-gray-200">
                  <SafeIcon icon={FiLock} className="text-lg text-red-500" />
                </div>
                <span className="font-medium text-gray-500">5 More Revenue Categories</span>
              </div>
              <span className="text-gray-500 hover:text-indigo-600">Unlock Full Report</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="h-2 rounded-full bg-indigo-300" style={{ width: '37%' }}></div>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-gray-200">
                  <SafeIcon icon={FiDollarSign} className="text-lg text-gray-600" />
                </div>
                <span className="font-bold text-gray-900">Preview Benefits</span>
              </div>
              <span className="text-xl font-bold text-indigo-600">
                {formatCurrency(previewBenefitsTotal)}
              </span>
            </div>

            {/* Locked total benefits */}
            <div
              className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-between cursor-pointer"
              onClick={() => setShowEmailModal(true)}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiLock} className="text-red-500" />
                <span className="text-gray-500 font-medium">Total Annual Benefits</span>
              </div>
              <span className="text-gray-500 hover:text-indigo-600">Unlock Report</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* CTA for email capture */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-2xl shadow-lg p-8 text-center"
      >
        <h4 className="text-xl font-bold text-gray-900 mb-4">
          Get Your Complete Brand ROI Analysis
        </h4>
        <p className="text-gray-600 mb-6">
          Access your full report with all 8 benefit categories, strategic recommendations, and implementation plan.
        </p>
        <button
          onClick={() => setShowEmailModal(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors inline-block"
        >
          Unlock My Complete Analysis
        </button>
      </motion.div>

      {/* Email capture modal */}
      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSubmit={handleEmailSubmit}
        calculatorType="branding"
        previewData={results}
      />
    </motion.div>
  );
};

export default BrandingResultsSection;